import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

// Client feedback: BOOKING_HOLD/BOOKING_RELEASE/PAYMENT are internal enum
// values, not something a hotel's travel-agent account client should have to
// decode. Display-only relabeling — the underlying txn_type value (and the
// debit/credit-hold mechanics it drives) is untouched.
const TXN_TYPE_LABELS = {
  BOOKING_HOLD: 'Credit Reserved',
  BOOKING_RELEASE: 'Credit Released',
  PAYMENT: 'Payment Received',
  ADJUSTMENT: 'Adjustment',
  CHECKOUT_CITY_LEDGER_TRANSFER: 'Invoice Billed',
  FOLIO_ROUTE_TRANSFER: 'Invoice Billed'
};
const txnTypeLabel = (txnType) => TXN_TYPE_LABELS[txnType] || txnType;

// "Billing Only" ledger view (default) shows just what the travel agent's
// own accounts team cares about -- what they were billed (an invoice
// transferred to their account at checkout) and what they've paid -- same
// distinction as PmsCorporateMaster.js. Credit Reserved/Released rows are
// real ledger activity (temporary credit-limit holds, not charges) but
// showing them by default reads as far more account activity than actually
// happened. "All Activity" still shows every row for tracing a
// credit-limit discrepancy or auditing the full history.
const BILLING_TXN_TYPES = new Set(['CHECKOUT_CITY_LEDGER_TRANSFER', 'FOLIO_ROUTE_TRANSFER', 'PAYMENT', 'ADJUSTMENT']);

const emptyForm = {
  agent_code: '',
  agent_name: '',
  contact_person: '',
  mobile_number: '',
  email: '',
  gst_number: '',
  pan_number: '',
  state_code: '',
  billing_address: '',
  commission_percent: '',
  credit_limit: '',
  is_active: true
};

// 15-char GSTIN format: 2-digit state + 10-char PAN + 1 entity + 1 'Z' + 1 check
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

function PmsTravelAgentMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [ledgerByAgent, setLedgerByAgent] = useState({});
  const [viewingLedgerAgent, setViewingLedgerAgent] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerViewMode, setLedgerViewMode] = useState('BILLING');
  const [paymentOpenId, setPaymentOpenId] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', reference_no: '', remarks: '' });
  const [paymentError, setPaymentError] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadRows = async () => {
    if (!selectedProperty?.id) { setRows([]); return; }
    try {
      const res = await fetch('/api/v1/pms/travel-agents?all=1', { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || 'Failed to load travel agents');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load travel agents');
    }
  };

  useEffect(() => {
    setError(''); setMessage(''); setEditingId(''); setForm(emptyForm);
    setLedgerByAgent({}); setPaymentOpenId(null);
    setPaymentForm({ amount: '', reference_no: '', remarks: '' });
    setPaymentError('');
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const resetForm = () => { setEditingId(''); setForm(emptyForm); };

  const onSave = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    if (!String(form.agent_code || '').trim()) return setError('Agent Code is required');
    if (!String(form.agent_name || '').trim()) return setError('Agent Name is required');
    const mobile = String(form.mobile_number || '').trim();
    if (mobile && !/^\d{10}$/.test(mobile)) return setError('Mobile number must be exactly 10 digits');
    const email = String(form.email || '').trim();
    if (email && !/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,10}$/.test(email)) return setError('Enter a valid email address');
    const commission = Number(form.commission_percent || 0);
    if (!Number.isFinite(commission) || commission < 0) return setError('Commission % must be >= 0');
    if (commission > 100) return setError('Commission % cannot exceed 100');
    const creditLimit = Number(form.credit_limit || 0);
    if (!Number.isFinite(creditLimit) || creditLimit < 0) return setError('Credit Limit must be >= 0');
    const gstin = String(form.gst_number || '').trim().toUpperCase();
    if (gstin && !GSTIN_REGEX.test(gstin)) return setError('GST Number must be a valid 15-character GSTIN');
    const pan = String(form.pan_number || '').trim().toUpperCase();
    if (pan && !PAN_REGEX.test(pan)) return setError('PAN must be a valid 10-character PAN');
    const stateCode = String(form.state_code || '').trim();
    if (stateCode && !/^\d{2}$/.test(stateCode)) return setError('State Code must be 2 digits');
    setError(''); setMessage('');
    try {
      const payload = {
        agent_code: String(form.agent_code || '').trim(),
        agent_name: String(form.agent_name || '').trim(),
        contact_person: String(form.contact_person || '').trim(),
        mobile_number: String(form.mobile_number || '').trim(),
        email: String(form.email || '').trim(),
        gst_number: gstin || null,
        pan_number: pan || null,
        state_code: stateCode || (gstin ? gstin.substring(0, 2) : null),
        billing_address: String(form.billing_address || '').trim() || null,
        commission_percent: Number(commission.toFixed(3)),
        credit_limit: Number(creditLimit.toFixed(2)),
        is_active: form.is_active !== false
      };
      const url = editingId ? `/api/v1/pms/travel-agents/${editingId}` : '/api/v1/pms/travel-agents';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save travel agent');
      setMessage(`Travel agent ${editingId ? 'updated' : 'created'} successfully`);
      resetForm();
      loadRows();
    } catch (err) {
      setError(err?.message || 'Failed to save travel agent');
    }
  };

  const onEdit = (row) => {
    setEditingId(String(row.travel_agent_id));
    setForm({
      agent_code: row.agent_code || '',
      agent_name: row.agent_name || '',
      contact_person: row.contact_person || '',
      mobile_number: row.mobile_number || '',
      email: row.email || '',
      gst_number: row.gst_number || '',
      pan_number: row.pan_number || '',
      state_code: row.state_code || '',
      billing_address: row.billing_address || '',
      commission_percent: Number(row.commission_percent || 0),
      credit_limit: Number(row.credit_limit || 0),
      is_active: row.is_active !== false
    });
    setMessage(''); setError('');
    scrollToForm();
  };

  // Ledger opens in a large popup rather than expanding inline — refetches
  // every time it's opened so the reconciled totals (Available Credit / Net
  // Outstanding) are never shown stale.
  const openLedger = async (row) => {
    setViewingLedgerAgent(row);
    setLedgerViewMode('BILLING');
    setLedgerLoading(true);
    try {
      const res = await fetch(`/api/v1/pms/travel-agents/${row.travel_agent_id}/ledger`, { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load ledger');
      setLedgerByAgent((prev) => ({ ...prev, [String(row.travel_agent_id)]: data }));
    } catch (err) {
      setError(err?.message || 'Failed to load ledger');
    } finally {
      setLedgerLoading(false);
    }
  };

  const closeLedgerModal = () => setViewingLedgerAgent(null);

  const downloadLedgerCsv = (agent) => {
    const led = ledgerByAgent[String(agent.travel_agent_id)] || {};
    const entries = [...(led.ledger || [])].reverse();
    if (!entries.length) return;
    const cols = [
      { label: 'Sl.no',      key: (_l, idx) => idx },
      { label: 'Txn Type',   key: (l) => txnTypeLabel(l.txn_type) || '' },
      { label: 'Reservation No', key: (l) => l.reservation_no || '' },
      { label: 'Reference',  key: (l) => `${l.reference_type || ''}${l.reference_id ? ` #${l.reference_id}` : ''}` },
      { label: 'Debit',      key: (l) => Number(l.debit_amount || 0).toFixed(2) },
      { label: 'Credit',     key: (l) => Number(l.credit_amount || 0).toFixed(2) },
      { label: 'Remarks',    key: (l) => l.remarks || '' },
      { label: 'Date',       key: (l) => String(l.created_at || '').slice(0, 19).replace('T', ' ') },
    ];
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const header = cols.map(c => esc(c.label)).join(',');
    // Same 500-row cap as the on-screen table — seed the running balance from
    // the full unbounded net so it still lands correctly for an agent with
    // more history than that (see the matching note on the table render below).
    // Billing Only walks billing rows only (billed − paid), as on screen.
    const isBilling = ledgerViewMode === 'BILLING';
    const inView = (l) => !isBilling || BILLING_TXN_TYPES.has(l.txn_type);
    const visibleNet = entries.filter(inView).reduce((s, l) => s + Number(l.debit_amount || 0) - Number(l.credit_amount || 0), 0);
    let running = Number((isBilling ? led.billing_net_outstanding : led.net_outstanding) ?? visibleNet) - visibleNet;
    let shownCount = 0;
    const rows = [];
    entries.forEach((l) => {
      if (!inView(l)) return;
      running += Number(l.debit_amount || 0) - Number(l.credit_amount || 0);
      shownCount += 1;
      rows.push([...cols.map(c => esc(c.key(l, shownCount))), esc(running.toFixed(2))].join(','));
    });
    const csv = [`${header},"Running Bal."`, ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TravelAgentLedger_${agent.agent_code}_${ledgerViewMode === 'BILLING' ? 'BillingOnly_' : 'AllActivity_'}${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openPayment = (agentId) => {
    setPaymentError('');
    setPaymentForm({ amount: '', reference_no: '', remarks: '' });
    setPaymentOpenId((prev) => (prev === String(agentId) ? null : String(agentId)));
  };

  const submitPayment = async (agentId) => {
    setPaymentError('');
    const amount = Number(paymentForm.amount || 0);
    if (!amount || amount <= 0) { setPaymentError('Enter a valid payment amount greater than zero'); return; }
    try {
      const res = await fetch(`/api/v1/pms/travel-agents/${agentId}/payments`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          amount,
          reference_no: String(paymentForm.reference_no || '').trim(),
          remarks: String(paymentForm.remarks || '').trim() || 'Travel agent payment received'
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Payment failed');
      setMessage(`Travel agent payment of ${amount.toFixed(2)} recorded successfully`);
      setPaymentOpenId(null);
      setPaymentForm({ amount: '', reference_no: '', remarks: '' });
      await loadRows();
      const key = String(agentId);
      if (ledgerByAgent[key]) {
        const lRes = await fetch(`/api/v1/pms/travel-agents/${agentId}/ledger`, { headers: headers() });
        const lData = await lRes.json().catch(() => ({}));
        if (lRes.ok) setLedgerByAgent((prev) => ({ ...prev, [key]: lData }));
      }
    } catch (err) {
      setPaymentError(err?.message || 'Payment failed');
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office - Travel Agent</h3>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      <div ref={formRef} className="filter-bar" style={{ marginBottom: '0.8rem', ...editHighlightStyle(editHighlight) }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: '0.5rem' }}>
          <input placeholder="Agent Code" value={form.agent_code} onChange={(e) => setForm({ ...form, agent_code: e.target.value })} />
          <input placeholder="Agent Name" value={form.agent_name} onChange={(e) => setForm({ ...form, agent_name: e.target.value })} />
          <input placeholder="Contact Person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
          <input type="tel" inputMode="numeric" maxLength={10} placeholder="Mobile (10 digits)" value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value.replace(/\D/g, '').slice(0, 10) })} />
          <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input type="text" placeholder="GSTIN (15 chars)" maxLength={15} value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })} title="15-character GSTIN e.g. 27ABCDE1234F1Z5" />
          <input type="text" placeholder="PAN (10 chars)" maxLength={10} value={form.pan_number} onChange={(e) => setForm({ ...form, pan_number: e.target.value.toUpperCase() })} title="10-character PAN" />
          <input type="text" placeholder="State Code (2 digit)" maxLength={2} value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value.replace(/\D/g, '').slice(0, 2) })} title="2-digit state code, auto-filled from GSTIN" />
          <input type="text" placeholder="Billing Address" value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} />
          <input type="text" inputMode="decimal" placeholder="Commission % (0–100)" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} />
          <input type="text" inputMode="decimal" placeholder="Credit Limit" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} />
          <select value={form.is_active ? 'Y' : 'N'} onChange={(e) => setForm({ ...form, is_active: e.target.value === 'Y' })}>
            <option value="Y">Active</option>
            <option value="N">Inactive</option>
          </select>
        </div>
        <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={onSave}>{editingId ? 'Update Agent' : 'Add Agent'}</button>
          <button onClick={resetForm}>Clear</button>
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            {/* position: sticky on each <th> individually (not <thead>) —
                the reliable cross-browser pattern for a border-collapse
                table; see the matching note on the ledger table below. */}
            <tr>
              {['Code', 'Name', 'Contact', 'GSTIN'].map((h) => (
                <th key={h} style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--theme-primary)', color: '#fff', padding: '0.45rem', textAlign: 'left' }}>{h}</th>
              ))}
              {['Commission %', 'Credit Limit', 'Credit Used', 'Available Credit'].map((h) => (
                <th key={h} style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--theme-primary)', color: '#fff', padding: '0.45rem', textAlign: 'right' }}>{h}</th>
              ))}
              {['Status', 'Action'].map((h) => (
                <th key={h} style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--theme-primary)', color: '#fff', padding: '0.45rem', textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.travel_agent_id}>
                <tr style={{ borderBottom: '1px solid var(--theme-border)' }}>
                  <td style={{ padding: '0.45rem' }}>{r.agent_code}</td>
                  <td style={{ padding: '0.45rem' }}>{r.agent_name}</td>
                  <td style={{ padding: '0.45rem' }}>{r.contact_person || r.mobile_number || '-'}</td>
                  <td style={{ padding: '0.45rem', fontFamily: 'monospace', fontSize: '0.82rem', color: r.gst_number ? '#1d4ed8' : '#a0aec0' }}>
                    {r.gst_number || '— not set —'}
                  </td>
                  <td style={{ padding: '0.45rem', textAlign: 'right' }}>{Number(r.commission_percent || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.45rem', textAlign: 'right' }}>
                    {Number(r.credit_limit || 0).toFixed(2)}
                  </td>
                  <td style={{ padding: '0.45rem', textAlign: 'right', color: Number(r.credit_used || 0) > 0 ? '#c05621' : '#1a202c', fontWeight: Number(r.credit_used || 0) > 0 ? 700 : 400 }}>
                    {Number(r.credit_used || 0).toFixed(2)}
                  </td>
                  <td style={{ padding: '0.45rem', textAlign: 'right', color: Number(r.available_credit || 0) < 0 ? '#c53030' : '#276749', fontWeight: 700 }}>
                    {Number(r.available_credit || 0).toFixed(2)}
                  </td>
                  <td style={{ padding: '0.45rem' }}>{r.is_active ? 'ACTIVE' : 'INACTIVE'}</td>
                  <td style={{ padding: '0.45rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button onClick={() => onEdit(r)}>Edit</button>
                    <button onClick={() => openLedger(r)}>View Ledger</button>
                    {Number(r.credit_used || 0) > 0 ? (
                      <button
                        onClick={() => openPayment(r.travel_agent_id)}
                        style={{ background: paymentOpenId === String(r.travel_agent_id) ? '#c05621' : '#276749', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.25rem 0.55rem', cursor: 'pointer' }}
                      >
                        {paymentOpenId === String(r.travel_agent_id) ? 'Cancel Payment' : 'Receive Payment'}
                      </button>
                    ) : null}
                  </td>
                </tr>

                {/* Payment Panel */}
                {paymentOpenId === String(r.travel_agent_id) ? (
                  <tr>
                    <td colSpan={10} style={{ padding: '0.6rem 0.7rem', background: '#f0fff4', borderBottom: '2px solid #9ae6b4' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#276749', marginBottom: '0.45rem' }}>
                        Receive Payment — {r.agent_code} | {r.agent_name}
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#2d3748', marginBottom: '0.45rem' }}>
                        Outstanding Credit: <strong style={{ color: '#c05621' }}>{Number(r.credit_used || 0).toFixed(2)}</strong>
                      </div>
                      {paymentError ? <div style={{ color: '#c53030', marginBottom: '0.4rem', fontSize: '0.82rem' }}>{paymentError}</div> : null}
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Amount *</div>
                          <input
                            type="number" min={0.01} step="0.01" max={Number(r.credit_used || 0)}
                            placeholder={`Max ${Number(r.credit_used || 0).toFixed(2)}`}
                            value={paymentForm.amount}
                            onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))}
                            style={{ width: '140px' }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Reference No (Cheque / NEFT / UTR)</div>
                          <input
                            type="text" placeholder="e.g. NEFT123456"
                            value={paymentForm.reference_no}
                            onChange={(e) => setPaymentForm((p) => ({ ...p, reference_no: e.target.value }))}
                            style={{ width: '190px' }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Remarks</div>
                          <input
                            type="text" placeholder="Payment remarks"
                            value={paymentForm.remarks}
                            onChange={(e) => setPaymentForm((p) => ({ ...p, remarks: e.target.value }))}
                            style={{ width: '210px' }}
                          />
                        </div>
                        <button
                          onClick={() => submitPayment(r.travel_agent_id)}
                          className="btn-success"
                          style={{ borderRadius: '4px', padding: '0.35rem 0.9rem', cursor: 'pointer', fontWeight: 600 }}
                        >
                          Submit Payment
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))}
            {!rows.length ? <tr><td colSpan={10} style={{ padding: '0.6rem' }}>No travel agents found</td></tr> : null}
          </tbody>
        </table>
      </div>

      {/* Ledger — large popup rather than an inline row-expansion, mirroring
          the Corporate Master ledger modal exactly (see that file for the
          fuller explanation of the fixed-header/scrolling-body layout). */}
      {viewingLedgerAgent ? (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeLedgerModal(); }}
        >
          <div style={{ width: 'min(1100px, 96vw)', maxHeight: '88vh', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <button
              onClick={closeLedgerModal}
              style={{ position: 'absolute', top: '0.6rem', right: '0.7rem', border: '1px solid #cbd5e0', background: '#fff', color: '#2d3748', borderRadius: '4px', padding: '0.1rem 0.5rem', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1 }}
            >
              ×
            </button>

            <div style={{ padding: '1rem 1.2rem 0.6rem', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '0.5rem', paddingRight: '1.8rem' }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--theme-primary)' }}>
                  Credit Ledger — {viewingLedgerAgent.agent_code} &nbsp;|&nbsp; {viewingLedgerAgent.agent_name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#4a5568' }}>
                    {ledgerLoading ? 'Loading...' : (() => {
                      const all = ledgerByAgent[String(viewingLedgerAgent.travel_agent_id)]?.ledger || [];
                      const shown = ledgerViewMode === 'BILLING' ? all.filter((l) => BILLING_TXN_TYPES.has(l.txn_type)) : all;
                      return shown.length === all.length ? `${all.length} entries` : `${shown.length} of ${all.length} entries`;
                    })()}
                  </div>
                  <button
                    onClick={() => downloadLedgerCsv(viewingLedgerAgent)}
                    disabled={!(ledgerByAgent[String(viewingLedgerAgent.travel_agent_id)]?.ledger || []).length}
                    style={{ padding: '0.3rem 0.7rem', borderRadius: '5px', border: '1px solid #48bb78', background: '#f0fff4', color: '#276749', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}
                  >
                    ⬇ Download CSV
                  </button>
                </div>
              </div>

              {/* Billing Only (default) shows just what a travel agent's
                  accounts team cares about -- invoices billed and payments
                  received. All Activity reveals Credit Reserved/Released
                  rows too. See BILLING_TXN_TYPES above. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 600 }}>View:</span>
                <button
                  onClick={() => setLedgerViewMode('BILLING')}
                  style={{
                    padding: '0.25rem 0.7rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    border: ledgerViewMode === 'BILLING' ? '1px solid #2b6cb0' : '1px solid #cbd5e0',
                    background: ledgerViewMode === 'BILLING' ? '#2b6cb0' : '#fff',
                    color: ledgerViewMode === 'BILLING' ? '#fff' : '#4a5568'
                  }}
                >
                  Billing Only
                </button>
                <button
                  onClick={() => setLedgerViewMode('ALL')}
                  style={{
                    padding: '0.25rem 0.7rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    border: ledgerViewMode === 'ALL' ? '1px solid #2b6cb0' : '1px solid #cbd5e0',
                    background: ledgerViewMode === 'ALL' ? '#2b6cb0' : '#fff',
                    color: ledgerViewMode === 'ALL' ? '#fff' : '#4a5568'
                  }}
                >
                  All Activity
                </button>
                <span style={{ fontSize: '0.72rem', color: '#a0aec0' }}>
                  {ledgerViewMode === 'BILLING' ? '— invoices billed & payments received only' : '— includes credit-hold activity'}
                </span>
              </div>

              {(() => {
                const led = ledgerByAgent[String(viewingLedgerAgent.travel_agent_id)] || {};
                const totalEarned = Number(led.ledger_total_debit || 0);
                const totalPaid   = Number(led.ledger_total_credit || 0);
                // Total Credit includes both real PAYMENT rows and
                // BOOKING_RELEASE rows (a credit-hold being cleared, never
                // actual money) — paymentsReceived isolates just the real
                // payments; see the matching note on Corporate Master.
                const paymentsReceived = Number(led.payments_received || 0);
                // Billing Only: billed − paid, active holds shown separately.
                const isBilling = ledgerViewMode === 'BILLING';
                const fullBalance = Number(led.net_outstanding ?? (totalEarned - totalPaid));
                const balance = isBilling ? Number(led.billing_net_outstanding ?? fullBalance) : fullBalance;
                const onHold = Number(led.credit_on_hold || 0);
                return (
                  <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap' }}>
                    <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                      <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Commission %</div>
                      <div style={{ fontWeight: 700, color: '#2d3748' }}>{Number(viewingLedgerAgent.commission_percent || 0).toFixed(3)}</div>
                    </div>
                    {isBilling ? (
                      <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Total Billed</div>
                        <div style={{ fontWeight: 700, color: '#c05621' }}>{Number(led.billing_total_debit ?? totalEarned).toFixed(2)}</div>
                      </div>
                    ) : (<>
                      <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Total Holds</div>
                        <div style={{ fontWeight: 700, color: '#c05621' }}>{totalEarned.toFixed(2)}</div>
                      </div>
                      <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Total Credit</div>
                        <div style={{ fontWeight: 700, color: '#276749' }}>{totalPaid.toFixed(2)}</div>
                      </div>
                    </>)}
                    <div style={{ background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                      <div style={{ fontSize: '0.7rem', color: '#276749', textTransform: 'uppercase' }}>Payments Received</div>
                      <div style={{ fontWeight: 700, color: '#276749' }}>{paymentsReceived.toFixed(2)}</div>
                    </div>
                    <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                      <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Outstanding</div>
                      <div style={{ fontWeight: 700, color: balance > 0 ? '#c05621' : '#276749' }}>{balance.toFixed(2)}</div>
                    </div>
                    {isBilling && Math.abs(onHold) > 0.009 && (
                      <div title="Credit reserved for bookings not yet checked out" style={{ background: '#fffaf0', border: '1px solid #fbd38d', borderRadius: '6px', padding: '0.3rem 0.8rem', minWidth: '130px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#975a16', textTransform: 'uppercase' }}>On Hold (Bookings)</div>
                        <div style={{ fontWeight: 700, color: '#975a16' }}>{onHold.toFixed(2)}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto', borderTop: '1px solid #e2e8f0', margin: '0 1.2rem 1.2rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '0.82rem' }}>
                <thead>
                  {/* position: sticky on each <th> individually (not <thead>) —
                      see the matching note on the Corporate Master ledger table. */}
                  <tr>
                    {['#', 'Txn Type', 'Reference'].map((h) => (
                      <th key={h} style={{ position: 'sticky', top: 0, zIndex: 1, background: '#2b6cb0', color: '#fff', padding: '0.4rem 0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                    {['Hold', 'Payment', 'Running Bal.'].map((h) => (
                      <th key={h} style={{ position: 'sticky', top: 0, zIndex: 1, background: '#2b6cb0', color: '#fff', padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                    {['Remarks', 'Date'].map((h) => (
                      <th key={h} style={{ position: 'sticky', top: 0, zIndex: 1, background: '#2b6cb0', color: '#fff', padding: '0.4rem 0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const led = ledgerByAgent[String(viewingLedgerAgent.travel_agent_id)] || {};
                    const entries = [...(led.ledger || [])].reverse();
                    // Same 500-row-cap correction as the Corporate Ledger panel —
                    // seed from the full unbounded net, not 0.
                    // Billing Only walks billing rows only (billed − paid);
                    // All Activity walks every row — see Corporate Master.
                    const isBilling = ledgerViewMode === 'BILLING';
                    const inView = (l) => !isBilling || BILLING_TXN_TYPES.has(l.txn_type);
                    const visibleNet = entries.filter(inView).reduce((s, l) => s + Number(l.debit_amount || 0) - Number(l.credit_amount || 0), 0);
                    const fullNet = Number((isBilling ? led.billing_net_outstanding : led.net_outstanding) ?? visibleNet);
                    let running = fullNet - visibleNet;
                    if (!entries.length) {
                      return (
                        <tr><td colSpan={8} style={{ padding: '0.6rem 0.5rem', color: '#718096' }}>{ledgerLoading ? 'Loading...' : 'No credit entries found for this agent.'}</td></tr>
                      );
                    }
                    let shownIdx = 0;
                    const renderedRows = [];
                    entries.forEach((l) => {
                      if (!inView(l)) return;
                      running += Number(l.debit_amount || 0) - Number(l.credit_amount || 0);
                      shownIdx += 1;
                      renderedRows.push(
                        <tr key={l.ledger_id} style={{ borderBottom: '1px solid #edf2f7', background: shownIdx % 2 === 1 ? '#fff' : '#f7fafc' }}>
                          <td style={{ padding: '0.32rem 0.5rem', color: '#718096' }}>{shownIdx}</td>
                          <td style={{ padding: '0.32rem 0.5rem', fontWeight: 500 }}>{txnTypeLabel(l.txn_type)}</td>
                          <td style={{ padding: '0.32rem 0.5rem' }}>
                            {/* reservation_no (e.g. RES-00019) is the identifier front desk/
                                accounts actually recognize — reference_type/reference_id is
                                an internal row id (e.g. CITY_LEDGER_TXN #19) meaningful only
                                for tracing the exact posting, so it's kept as secondary text. */}
                            {l.reservation_no ? (
                              <div style={{ fontWeight: 600, color: '#2b6cb0' }}>{l.reservation_no}</div>
                            ) : null}
                            <div style={l.reservation_no ? { fontSize: '0.72rem', color: '#a0aec0' } : undefined}>
                              {l.reference_type || '-'}
                              {l.reference_id ? <span style={{ color: l.reservation_no ? '#a0aec0' : '#2b6cb0', marginLeft: '0.3rem' }}>#{l.reference_id}</span> : null}
                            </div>
                          </td>
                          <td style={{ padding: '0.32rem 0.5rem', textAlign: 'right', color: Number(l.debit_amount || 0) > 0 ? '#c05621' : '#a0aec0' }}>
                            {Number(l.debit_amount || 0) > 0 ? Number(l.debit_amount).toFixed(2) : '-'}
                          </td>
                          <td style={{ padding: '0.32rem 0.5rem', textAlign: 'right', color: Number(l.credit_amount || 0) > 0 ? '#276749' : '#a0aec0' }}>
                            {Number(l.credit_amount || 0) > 0 ? Number(l.credit_amount).toFixed(2) : '-'}
                          </td>
                          <td style={{ padding: '0.32rem 0.5rem', textAlign: 'right', fontWeight: 600, color: running > 0 ? '#c05621' : '#276749' }}>
                            {running.toFixed(2)}
                          </td>
                          <td style={{ padding: '0.32rem 0.5rem', color: '#4a5568', minWidth: '220px', maxWidth: '360px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {l.remarks || '-'}
                          </td>
                          <td style={{ padding: '0.32rem 0.5rem', whiteSpace: 'nowrap', color: '#4a5568' }}>
                            {String(l.created_at || '').slice(0, 19).replace('T', ' ')}
                          </td>
                        </tr>
                      );
                    });
                    return renderedRows.length ? renderedRows : (
                      <tr><td colSpan={8} style={{ padding: '0.6rem 0.5rem', color: '#718096' }}>No billing activity yet for this agent — switch to "All Activity" to see credit-hold activity.</td></tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PmsTravelAgentMaster;
